<template>
	<div>
		<section class="main-content contact-section">
			<div class="row">
				<div class="columns column6">
					<h2>{{ defaults[locale].contactForm.header }}</h2>
					<div v-parse-links v-html="defaults[locale].contactForm.content" />
					<form ref="contactform" method="post" class="contact-form" @submit.prevent="submit()">
						<span
							v-if="state.error"
							class="error"
							v-html="$t(`form${capitalize(state.error)}`, { email: defaults[locale].website.email })"
						></span>
						<div v-for="field in form" :key="field.name">
							<div v-if="field.edittype == 'textarea'">
								<label :for="field.name">{{ $t(`form${capitalize(field.name)}`) }}</label>
								<textarea
									:id="field.name"
									:key="field.name"
									v-model="field.value"
									:name="field.name"
									:class="{ required: field.required }"
									:required="field.required"
								/>
							</div>
							<div v-else-if="field.edittype == 'checkbox'" class="checkbox-container">
								<input
									:id="field.name"
									v-model="field.value"
									:name="field.name"
									type="checkbox"
									:checked="!!field.value"
								/>
								<label :for="field.name">{{ $t(`form${capitalize(field.name)}`) }}</label>
							</div>
							<div v-else-if="field.edittype == 'list'" class="select">
								<select :id="field.name" v-model="state.subject" :name="field.name">
									<option value="">
										{{ $t('formChooseSubject') }}
									</option>
									<option v-for="(value, key) in field.options" :key="key" :value="value">
										{{ $t(`form${capitalize(value)}`) }}
									</option>
								</select>
							</div>
							<div v-else>
								<input
									:key="field.name"
									v-model="field.value"
									:name="field.name"
									:required="field.required"
									:type="state.inputTypes[field.name] || 'text'"
									:placeholder="$t(`form${capitalize(field.name)}`)"
								/>
							</div>
						</div>
						<input type="submit" class="button button-cta action" :value="$t('formSubmit')" />
					</form>
				</div>
				<div class="border" />
				<div class="columns column6">
					<picture>
						<source :srcset="defaults[locale].contactForm.imageWebp" type="image/webp" />
						<source :srcset="defaults[locale].contactForm.image" />
						<img
							:src="defaults[locale].contactForm.image"
							:alt="defaults[locale].contactForm.imageAlt"
							loading="lazy"
						/>
					</picture>
				</div>
			</div>
		</section>
	</div>
</template>

<script setup>
const { locale } = useI18n();
const { defaults, fetchDefaults } = useDefaults();
const { $installRecaptcha, $executeRecaptcha } = useNuxtApp();

await fetchDefaults();

const contactform = ref(null);

const props = defineProps({
	form: { type: Array, default: () => [] },
	name: { type: String, default: '' },
});

const state = useState('state', () => ({
	inputTypes: { email: 'email', phone: 'tel' },
	error: '',
	subject: '',
}));

const submit = async () => {
	const data = props.form.reduce((result, item) => ({ ...result, [item.name]: item.value ?? '' }), {});

	data.language = locale.value;
	data.subject = state.value.subject;

	if (window.recaptcha) {
		data['g-recaptcha-response'] = await $executeRecaptcha();
	}

	const { data: result } = await useWebsiteFetch(props.name, {
		method: 'POST',
		body: JSON.stringify(data),
		query: { language: locale.value },
	});

	if (result.value.status === 'error') {
		state.value.error = result.error;
	} else if (result.value.redirect) {
		contactform.value.reset();
		navigateTo(result.value.redirect);
	}
};

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

onMounted(async () => {
	await $installRecaptcha();
});
</script>

<style scoped lang="scss">
.error {
	color: red;
}

.contact-section {
	h2 {
		font-family: MessinaSerif, serif;
		margin: 0 0 20px;
	}

	p {
		text-transform: uppercase;
	}
}

.row {
	border-top: 1px solid #000;
	border-bottom: 1px solid #000;
	display: flex;
	flex-flow: row nowrap;
	justify-content: space-between;
	align-items: center;
	padding: 80px 0;
	width: 96%;
	max-width: 1280px !important;

	.border {
		width: 1px;
		background: #000;
		align-self: stretch;
	}

	.column6 {
		padding: 0 120px 0 30px;

		&:last-child {
			padding: 0 30px 0 120px;
		}
	}
}

form {
	margin-top: 30px;

	input,
	textarea,
	label,
	select {
		width: 100%;
		padding: 15px 10px 15px 0;
		margin: 10px 0;
		border: none;
		border-bottom: 1px solid #000;
		background: none;
		text-transform: uppercase;
		font-family: 'courier_newregular', MessinaSerif, serif;
		outline: none;
		font-size: 16px;
	}

	input,
	textarea {
		text-transform: none;
	}

	textarea {
		border: 1px solid #000;
		padding: 10px;
		min-height: 250px;
	}

	input.button {
		width: auto;
		border: 1px solid;
		border-color: $primary-color;
		color: $primary-color;
		padding: 14px 35px 12px;
		text-transform: none;
		cursor: pointer;

		&:hover {
			color: #fff;
			background: $primary-color;
		}
	}

	label {
		padding: 25px 0 5px;
		display: block;
		border: none;
		text-transform: uppercase;
	}

	select {
		background: url('~/assets/images/select-field-arrow.png') center right no-repeat #f7f5eb;
		background-size: 18px;
		appearance: none;
		width: 100%;
		color: #000;
	}
}

.checkbox-container {
	display: flex;
	margin: 5px 0 10px;
	align-items: center;
	justify-content: space-between;

	label,
	input {
		width: auto;
		padding: 0;
		font-size: 0.9em;
	}

	label {
		margin: 2px 5px -1px;
		width: calc(100% - 40px);
		font-size: 14px;
		text-transform: uppercase;
	}

	input {
		appearance: none;
		background: none;
		padding: 0;
		outline: none;
		width: 17px;
		height: 17px;
		border: 1px solid #000;

		&:checked {
			background: url('~/assets/images/checkbox-checked.jpg') center center no-repeat;
			background-size: 17px;
		}
	}
}

::placeholder {
	color: #000;
	opacity: 1;
	text-transform: uppercase;
}

@media (max-width: 900px) {
	.contact-section {
		text-align: center;

		label {
			text-align: left;
		}
	}

	.row {
		flex-flow: column wrap;
		padding: 20px 0;

		.border {
			display: none;
		}

		.column6 {
			padding: 10px 20px;

			&:last-child {
				border-top: 1px solid #000;
				padding: 40px 0 10px;
				margin: 20px auto 0;
			}
		}
	}
}
</style>
